/**
 * Cross-tab broadcast helper · web implementation.
 *
 * Web wraps `globalThis.BroadcastChannel` when available. Older Safari
 * (and any environment without BroadcastChannel) gets a no-op handle
 * with `supported: false` — the cache layer simply skips the broadcast
 * step when `supported === false`.
 *
 * The factory is consumed via `adapter.broadcast.openBroadcast(name)` —
 * an `8th` platform-adapter slot. Direct imports of this file from core
 * code are forbidden by `.claude/rules/platform-agnostic.md`.
 */

import type { BroadcastHandle } from '../../cache/broadcast/broadcast-helper.js';

interface BroadcastChannelEvent {
  readonly data: unknown;
}

interface BroadcastChannelLike {
  postMessage(msg: unknown): void;
  addEventListener(type: 'message', listener: (event: BroadcastChannelEvent) => void): void;
  close(): void;
}

interface BroadcastChannelCtor {
  new (name: string): BroadcastChannelLike;
}

function getCtor(): BroadcastChannelCtor | undefined {
  const g = globalThis as { BroadcastChannel?: BroadcastChannelCtor };
  return g.BroadcastChannel;
}

export function openBroadcast(channelName: string): BroadcastHandle {
  const Ctor = getCtor();
  if (Ctor === undefined) {
    return {
      supported: false,
      postMessage(): void {},
      close(): void {},
      addEventListener(): void {},
    };
  }
  const ch = new Ctor(channelName);
  return {
    supported: true,
    postMessage(msg): void {
      ch.postMessage(msg);
    },
    close(): void {
      ch.close();
    },
    addEventListener(_type, listener): void {
      ch.addEventListener('message', (e) => listener({ data: e.data }));
    },
  };
}
